/**
 * BookingCard Component
 * Displays a single booking with all its details and action buttons
 */
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Clock, ClipboardList, Edit, ExternalLink, Send, CalendarPlus, 
  UserPlus, FileText, MessageSquare, Trash2, Percent 
} from 'lucide-react';

export default function BookingCard({
  booking,
  formatDate,
  formatTime,
  onStatusChange,
  onManageEvent,
  onEdit,
  onCopyPortalLink,
  onSendEmail,
  onExportCalendar,
  onAssignEmployees,
  onGenerateInvoice,
  onViewPortal,
  onDelete
}) {
  const getStatusClasses = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-500/20 text-green-500';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-500';
      case 'completed':
        return 'bg-gray-500/20 text-gray-400';
      case 'cancelled':
        return 'bg-red-500/20 text-red-500';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const amountDue = Math.max(0, (booking.total_amount || 0) - (booking.paid_amount || 0));

  return (
    <div
      className="glass-card-hover rounded-2xl p-6"
      data-testid={`booking-item-${booking.id}`}
    >
      {/* Header: Client Info & Status */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-heading text-xl font-semibold mb-1">{booking.client_name}</h3>
          <p className="text-sm text-muted-foreground">{booking.client_email}</p>
          {booking.last_portal_login ? (
            <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
              Last portal login: {new Date(booking.last_portal_login).toLocaleDateString()} at {new Date(booking.last_portal_login).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground/60 mt-1 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
              Never logged into portal
            </p>
          )}
        </div>
        <Select
          value={booking.status}
          onValueChange={(value) => onStatusChange(booking.id, value, booking)}
        >
          <SelectTrigger 
            className={`w-[130px] h-8 text-xs font-medium border-0 ${getStatusClasses(booking.status)}`}
            data-testid={`booking-status-${booking.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                Pending
              </span>
            </SelectItem>
            <SelectItem value="confirmed">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Confirmed
              </span>
            </SelectItem>
            <SelectItem value="completed">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                Completed
              </span>
            </SelectItem>
            <SelectItem value="cancelled">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                Cancelled
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Event Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 text-sm">
        <div>
          <span className="block text-xs text-muted-foreground">Event Date</span>
          <span className="text-foreground font-medium">
            {formatDate(booking.event_date)}
            {booking.event_date && (
              <span className="ml-1 text-muted-foreground font-normal">
                ({new Date(booking.event_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })})
              </span>
            )}
          </span>
        </div>
        <div>
          <span className="block text-xs text-muted-foreground">Event Time</span>
          <span className="text-foreground font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {booking.start_time ? formatTime(booking.start_time) : '--:--'} - {booking.end_time ? formatTime(booking.end_time) : '--:--'}
          </span>
        </div>
        <div>
          <span className="block text-xs text-muted-foreground">Venue</span>
          <span className="text-foreground font-medium">{booking.venue}</span>
        </div>
        <div>
          <span className="block text-xs text-muted-foreground">Package</span>
          <span className="text-foreground font-medium">{booking.package}</span>
        </div>
      </div>
      
      {/* Financial Summary */}
      <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-muted/30 rounded-lg">
        <div>
          <span className="block text-xs text-muted-foreground">Total Amount</span>
          <span className="text-foreground font-semibold">${booking.total_amount || 0}</span>
          {booking.discount_amount > 0 && (
            <span className="block text-xs text-green-500">(-${booking.discount_amount} discount)</span>
          )}
        </div>
        <div>
          <span className="block text-xs text-muted-foreground">Total Paid</span>
          <span className="text-green-500 font-semibold">${booking.paid_amount || 0}</span>
        </div>
        <div>
          <span className="block text-xs text-muted-foreground">Amount Due</span>
          <span className={`font-semibold ${amountDue > 0 ? 'text-amber-500' : 'text-green-500'}`}>
            ${amountDue}
          </span>
        </div>
      </div>
      
      {/* Applied Discounts */}
      {booking.applied_discounts?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {booking.applied_discounts.map((discount, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400">
              <Percent className="w-3 h-3" />
              {discount.name}
              {discount.auto_applied && (
                <span className="ml-1 px-1 bg-green-500/30 rounded text-green-300 text-[10px]">AUTO</span>
              )}
              {discount.tags?.map(tag => (
                <span key={tag} className="ml-1 px-1 bg-blue-500/30 rounded text-blue-300">{tag}</span>
              ))}
            </span>
          ))}
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-4 border-t border-border flex-wrap">
        <Button
          size="sm"
          onClick={() => onManageEvent(booking)}
          className="bg-neon-purple hover:bg-neon-purple/80 text-white"
          data-testid={`manage-event-btn-${booking.id}`}
        >
          <ClipboardList className="w-4 h-4 mr-2" />
          Manage Event
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit(booking)}
          className="border-border hover:bg-muted/50"
          data-testid={`edit-booking-btn-${booking.id}`}
        >
          <Edit className="w-4 h-4 mr-2" />
          Edit
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onCopyPortalLink(booking.client_portal_token)}
          className="border-border hover:bg-muted/50"
          data-testid={`copy-portal-link-btn-${booking.id}`}
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Copy Portal Link
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onSendEmail(booking)}
          className="border-neon-purple/30 text-neon-purple hover:bg-neon-purple/10"
          data-testid={`send-email-btn-${booking.id}`}
        >
          <Send className="w-4 h-4 mr-2" />
          Email
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onExportCalendar(booking)}
          className="border-border hover:bg-muted/50"
          data-testid={`export-calendar-btn-${booking.id}`}
        >
          <CalendarPlus className="w-4 h-4 mr-2" />
          Add to Calendar
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAssignEmployees(booking)}
          className="border-border hover:bg-muted/50"
          data-testid={`assign-employees-btn-${booking.id}`}
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Employees ({booking.assigned_employees?.length || 0})
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onGenerateInvoice(booking)}
          className="border-border hover:bg-muted/50"
          data-testid={`generate-invoice-btn-${booking.id}`}
        >
          <FileText className="w-4 h-4 mr-2" />
          Invoice
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onViewPortal(booking)}
          className="border-border hover:bg-muted/50"
          data-testid={`view-portal-btn-${booking.id}`}
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Portal
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDelete(booking)}
          className="border-red-500/30 text-red-500 hover:bg-red-500/10 hover:text-red-400"
          data-testid={`delete-booking-btn-${booking.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
